import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Award, BookOpen, GraduationCap, ClipboardCheck, Lightbulb, PlusCircle } from 'lucide-react';
import ExamManagement from './ExamManagement';
import CourseInstanceManagement from './CourseInstanceManagement';
import SensorManagement from './SensorManagement';
import GradingOverview from './GradingOverview';
import NewExamSetWizard from './wizards/NewExamSetWizard';
import NewCourseInstanceWizard from './wizards/NewCourseInstanceWizard';

type TabType = 'exams' | 'courses' | 'sensors' | 'grading';
type WizardType = 'exam-set' | 'course-instance' | null;

const VALID_TABS: TabType[] = ['exams', 'courses', 'sensors', 'grading'];

export default function CoursesAndExams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'exams'
  );
  const [openWizard, setOpenWizard] = useState<WizardType>(null);
  const [preselectedExamSetId, setPreselectedExamSetId] = useState<string | undefined>(undefined);

  // Sync URL with active tab
  useEffect(() => {
    if (tabParam && VALID_TABS.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const tabs = [
    {
      id: 'exams' as TabType,
      label: 'Prøvesystem',
      icon: <Award size={18} />,
      description: 'Administrer prøvesett og eksamensspørsmål'
    },
    {
      id: 'courses' as TabType,
      label: 'Kurs',
      icon: <BookOpen size={18} />,
      description: 'Administrer kursgjennomføringer og kandidater'
    },
    {
      id: 'sensors' as TabType,
      label: 'Sensorer',
      icon: <GraduationCap size={18} />,
      description: 'Administrer sensorer for sensur av prøver'
    },
    {
      id: 'grading' as TabType,
      label: 'Til sensur',
      icon: <ClipboardCheck size={18} />,
      description: 'Oversikt over besvarelser som venter på sensur'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Kurs og prøver</h1>
        <p className="text-gray-600 mt-2">
          Administrer kursgjennomføringer, prøvesett og sensorer
        </p>
      </div>

      {/* Veiviser-boks — rekkefølge + CTA for guided opprettelse */}
      <div className="rounded-xl border border-kf-blue-200 bg-gradient-to-r from-kf-blue-50 to-kf-orange-50/40 p-4 sm:p-5 shadow-sm">
        <div className="flex items-start gap-3 mb-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-white/80 border border-kf-blue-200 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-kf-orange-500" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-kf-gray-900">Veivisere for guided opprettelse</h2>
            <p className="text-sm text-kf-gray-700 mt-1">
              Her finner du veivisere for å opprette prøvesett og kurs steg for steg. <strong>Rekkefølgen</strong> er at
              du oppretter et prøvesett knyttet til en godkjent studieplan først, deretter oppretter en kursgjennomføring
              som bruker prøvesettet. Veiviseren kan starte fra bunnen eller fylle ut forslag med KI.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:ml-12">
          <button
            type="button"
            onClick={() => {
              setPreselectedExamSetId(undefined);
              setOpenWizard('exam-set');
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            Veiviser for nytt prøvesett
          </button>
          <button
            type="button"
            onClick={() => {
              setPreselectedExamSetId(undefined);
              setOpenWizard('course-instance');
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-kf-blue-300 hover:bg-kf-blue-50 text-kf-blue-700 text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            Veiviser for ny kursgjennomføring
          </button>
        </div>
      </div>

      {/* Tab Navigation — wrapper til flere rader ved behov, ingen scrollbar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-x-4 sm:gap-x-8 gap-y-0" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                group inline-flex items-center gap-2 py-3 sm:py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-kf-primary text-kf-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              title={tab.description}
            >
              <span className={`${activeTab === tab.id ? 'text-kf-primary' : 'text-gray-400 group-hover:text-gray-500'}`}>
                {tab.icon}
              </span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'exams' && <ExamManagement />}
        {activeTab === 'courses' && <CourseInstanceManagement />}
        {activeTab === 'sensors' && <SensorManagement />}
        {activeTab === 'grading' && <GradingOverview />}
      </div>

      {/* Veivisere (overlay modals) */}
      <NewExamSetWizard
        open={openWizard === 'exam-set'}
        onClose={() => setOpenWizard(null)}
        onComplete={(examSetId) => {
          // Fra prøvesett-veiviser kan vi hoppe rett inn i kurs-veiviser
          setPreselectedExamSetId(examSetId);
          setOpenWizard('course-instance');
        }}
      />
      <NewCourseInstanceWizard
        open={openWizard === 'course-instance'}
        onClose={() => {
          setOpenWizard(null);
          setPreselectedExamSetId(undefined);
        }}
        preselectedExamSetId={preselectedExamSetId}
      />
    </div>
  );
}
